// Mock AuthContext for the invoicing demo.
// Preserves the surface of the original (login, logout, isAdmin, ProtectedRoute, etc.)
// but accepts any credentials and never calls a real backend.
import React, { useEffect, useState, createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'

export type LearningPreference = 'always_ask' | 'always_learn' | 'never_learn'

export interface User {
  id: string
  email: string
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: 'admin' | 'regular' | 'super_admin'
  status: 'active' | 'pending' | 'disabled'
  learning_preference: LearningPreference
  first_login_at?: string
  created_at: string
  updated_at: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  logout: (redirectToLogin?: boolean) => void
  loading: boolean
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  isActive: () => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem('user_data')
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

function makeUser(email: string): User {
  const trimmed = email.trim() || 'demo@superstate.one'
  const name = trimmed
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Demo'
  const now = new Date().toISOString()
  return {
    id: 'mock-user',
    email: trimmed,
    full_name: name,
    is_active: true,
    is_superuser: true,
    role: 'admin',
    status: 'active',
    learning_preference: 'always_ask',
    created_at: now,
    updated_at: now,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('access_token'))
  const [user, setUser] = useState<User | null>(() => readStoredUser())
  const [loading, setLoading] = useState(false)

  const login = async (email: string, password: string, rememberMe = false) => {
    setLoading(true)
    try {
      // Any non-empty credentials succeed
      const newUser = makeUser(email || 'demo@superstate.one')
      const newToken = 'mock-access-token'
      setToken(newToken)
      setUser(newUser)
      const sessionDuration = rememberMe
        ? 90 * 24 * 60 * 60 * 1000
        : 7 * 24 * 60 * 60 * 1000
      localStorage.setItem('access_token', newToken)
      localStorage.setItem('refresh_token', 'mock-refresh-token')
      localStorage.setItem('user_data', JSON.stringify(newUser))
      localStorage.setItem('remember_me', String(rememberMe))
      localStorage.setItem('session_expiry', String(Date.now() + sessionDuration))
      void password
    } finally {
      setLoading(false)
    }
  }

  const requestPasswordReset = async () => {}
  const resetPassword = async () => {}
  const changePassword = async () => {}

  const logout = (redirectToLogin = true) => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('session_expiry')
    localStorage.removeItem('remember_me')
    if (redirectToLogin) window.location.href = '/login'
  }

  // Re-hydrate when localStorage changes from another tab
  useEffect(() => {
    const handler = () => {
      setToken(localStorage.getItem('access_token'))
      setUser(readStoredUser())
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin' || false
  const isSuperAdmin = () => user?.role === 'super_admin' || false
  const isActive = () => user?.status === 'active' || false

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        requestPasswordReset,
        resetPassword,
        changePassword,
        logout,
        loading,
        isAdmin,
        isSuperAdmin,
        isActive,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!token || !user) navigate('/login', { replace: true })
  }, [token, user, navigate])

  if (!token || !user) return null
  return <>{children}</>
}
